import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { postReview } from "../api/review";

const ReviewModal = ({
  isOpen,
  onClose,
  order,
  user,
  token,
  fetchAndSetReviews,
  userReviews
}) => {
  const [activeProductId, setActiveProductId] = useState(null);
  const [newReviewRating, setNewReviewRating] = useState(0);
  const [newReviewText, setNewReviewText] = useState("");
  const [reviewFormError, setReviewFormError] = useState(null);
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);
  const navigate = useNavigate();
  

  // Set default selected product when modal opens
  useEffect(() => {
    if (order?.items?.length > 0) {
      setActiveProductId(order.items[0].product_id);
    }
  }, [order]);

  const currentItem = order?.items?.find(
    (item) => item.product_id === activeProductId
  );

  const existingReview = userReviews?.find(
    (r) => r.product_id === activeProductId
  );

  useEffect(() => {
    if (existingReview) {
      setNewReviewText(existingReview.review);
      setNewReviewRating(Number(existingReview.rating));
    } else {
      setNewReviewText("");
      setNewReviewRating(0);
    }
  }, [activeProductId]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setReviewFormError(null);

    if (!user || !token) {
      toast.error("You must be logged in to post a review.");
      navigate("/login");
      return;
    }
    if (!newReviewText.trim()) {
      setReviewFormError("Review comment cannot be empty.");
      return;
    }
    if (newReviewRating === 0) {
      setReviewFormError("Please select a rating.");
      return;
    }

    if (!activeProductId) {
      toast.error("Product not selected.");
      return;
    }

    setIsReviewSubmitting(true);
    try {
      const response = await postReview(
        activeProductId,
        user._id,
        newReviewRating,
        newReviewText,
        user.name,
        user.email,
        token
      );

      if (response.message === "Review submitted") {
        toast.success("Review submitted successfully!");
        await fetchAndSetReviews();
      } else {
        toast.error(response.message);
        setReviewFormError(response.message);
      }
    } catch (error) {
      toast.error("Failed to submit review.");
      setReviewFormError("An error occurred.");
    } finally {
      setIsReviewSubmitting(false);
    }
  };

  if (!isOpen || !order) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-lg relative border border-purple-600 shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-2 right-3 text-gray-400 hover:text-white"
        >
          ✕
        </button>
        <h2 className="text-xl font-bold text-purple-300 mb-4 text-center">
          {existingReview ? "Your Review" : "Add Review"}
        </h2>

        {/* Product Selector */}
        <select
          value={activeProductId || ""}
          onChange={(e) => setActiveProductId(e.target.value)}
          className="w-full mb-4 p-3 bg-gray-800 text-white rounded-lg border border-purple-700"
        >
          {order.items.map((item) => (
            <option key={item.product_id} value={item.product_id}>
              {item.product_name}
            </option>
          ))}
        </select>

        {existingReview ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-yellow-400">Rating:</p>
              <p className="text-white">{existingReview.rating} / 5</p>
            </div>
            <div>
              <p className="font-semibold text-purple-400">Comment:</p>
              <p className="text-white/80 mt-2 bg-gray-800 p-4 rounded-xl border border-purple-700">
                {existingReview.review}
              </p>
            </div>
            <div className="text-right text-xs text-white/50">
              {existingReview.date_time}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmitReview} className="space-y-6">
            <div>
              <label className="block text-lg font-semibold text-purple-300 mb-3">
                Your Rating:
              </label>
              <div className="flex space-x-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={30}
                    fill={newReviewRating >= star ? "#FACC15" : "none"}
                    stroke={
                      newReviewRating >= star ? "#FACC15" : "currentColor"
                    }
                    className={`cursor-pointer transition-all duration-200 ${
                      newReviewRating >= star
                        ? "text-yellow-400 drop-shadow-glow"
                        : "text-gray-500 hover:text-yellow-300"
                    }`}
                    onClick={() => setNewReviewRating(star)}
                  />
                ))}
              </div>
              {reviewFormError?.includes("rating") && (
                <p className="text-pink-400 text-sm mt-1">{reviewFormError}</p>
              )}
            </div>

            <div>
              <label className="block text-lg font-semibold text-purple-300 mb-3">
                Your Comment:
              </label>
              <textarea
                className="w-full p-4 rounded-2xl bg-gradient-to-r from-gray-900 to-gray-800 text-white border border-purple-700 focus:ring-2 focus:ring-pink-500 min-h-[120px] placeholder-gray-400 transition-all duration-300"
                value={newReviewText}
                onChange={(e) => setNewReviewText(e.target.value)}
                placeholder="Write your review..."
              />
              {reviewFormError?.includes("comment") && (
                <p className="text-pink-400 text-sm mt-1">{reviewFormError}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isReviewSubmitting}
              className={`w-full px-6 py-3 rounded-2xl text-lg font-bold tracking-wide flex items-center justify-center gap-3 shadow-lg transition-all duration-300 ${
                isReviewSubmitting
                  ? "bg-gray-700 text-gray-300 cursor-not-allowed"
                  : "bg-gradient-to-r from-teal-500 to-green-600 hover:from-green-600 hover:to-teal-500 text-white shadow-green-500/30 hover:shadow-lg"
              }`}
            >
              {isReviewSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    ></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                "Submit Review"
              )}
            </button>

            {reviewFormError &&
              !reviewFormError.includes("rating") &&
              !reviewFormError.includes("comment") && (
                <p className="text-pink-400 text-sm mt-2">{reviewFormError}</p>
              )}
          </form>
        )}
      </div>
    </div>
  );
};

export default ReviewModal;
